import { useRef, useState, type ElementRef, type FormEvent } from 'react'
import {
	BasisTheoryProvider,
	CardExpirationDateElement,
	CardNumberElement,
	CardVerificationCodeElement,
	useBasisTheory,
} from '@basis-theory/basis-theory-react'
import { AlertCircle, Loader2, Lock } from 'lucide-react'

import { Button } from '@/components/ui/button'

/**
 * Card capture for accounts billed through our vault (Checkout.com today).
 *
 * The three fields are Basis Theory iframes: the card number never exists in
 * the CRM's JavaScript. On submit the card is tokenized straight into OUR
 * vault and only the token id is handed to the parent, which asks the server
 * to verify it ($0, 3-D Secure) on the acquirer. No acquirer script runs
 * here, so changing acquirer never touches this form or the saved cards.
 */

interface VaultCardSetupProps {
	/** Browser key of the vault (from `billing.createSetupSession`). */
	publicKey: string
	/** Stamped on the token so the server knows it comes from this form. */
	tokenPurpose: string
	/** Called with the vault token id once the card is tokenized. */
	onTokenized: (token: string) => void
	/** The parent is verifying the card or redirecting to 3-D Secure. */
	isFinalizing?: boolean
	/** Server-side refusal to show under the form (declined card…). */
	error?: string | null
	submitLabel?: string
}

// Rendered inside cross-origin iframes: `inherit` would resolve to the
// iframe's serif default, so the app's system stack is passed explicitly.
const elementStyle = {
	fonts: [],
	base: {
		color: '#0f172a',
		fontSize: '15px',
		fontFamily:
			'-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
		'::placeholder': { color: '#94a3b8' },
	},
	invalid: { color: '#dc2626' },
}

const fieldClass = 'border-input bg-background rounded-md border px-3 py-2.5'

export function VaultCardSetup({ publicKey, ...props }: VaultCardSetupProps) {
	const { bt, error } = useBasisTheory(publicKey, { elements: true })

	if (error) {
		return (
			<p className="text-destructive py-6 text-center text-sm">
				We couldn't load the secure card form. Please reload the page.
			</p>
		)
	}
	if (!bt) {
		return (
			<div className="flex items-center justify-center gap-2 py-10">
				<Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
				<span className="text-muted-foreground text-sm">Preparing secure card form…</span>
			</div>
		)
	}
	return (
		<BasisTheoryProvider bt={bt}>
			<VaultCardForm {...props} />
		</BasisTheoryProvider>
	)
}

function VaultCardForm({
	tokenPurpose,
	onTokenized,
	isFinalizing,
	error: serverError,
	submitLabel,
}: Omit<VaultCardSetupProps, 'publicKey'>) {
	const { bt } = useBasisTheory()
	const numberRef = useRef<ElementRef<typeof CardNumberElement>>(null)
	const expiryRef = useRef<ElementRef<typeof CardExpirationDateElement>>(null)
	const cvcRef = useRef<ElementRef<typeof CardVerificationCodeElement>>(null)

	const [cardBrand, setCardBrand] = useState<string | undefined>(undefined)
	const [complete, setComplete] = useState({ number: false, expiry: false, cvc: false })
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const busy = isSubmitting || Boolean(isFinalizing)
	const isComplete = complete.number && complete.expiry && complete.cvc
	const shownError = error ?? serverError ?? null

	const handleSubmit = async (event: FormEvent) => {
		event.preventDefault()
		if (!bt || !numberRef.current || !expiryRef.current || !cvcRef.current || busy) return
		if (!isComplete) {
			setError('Please complete your card details.')
			return
		}

		setIsSubmitting(true)
		setError(null)
		try {
			const token = await bt.tokens.create({
				type: 'card',
				data: {
					number: numberRef.current,
					expiration_month: expiryRef.current.month(),
					expiration_year: expiryRef.current.year(),
					cvc: cvcRef.current,
				},
				metadata: { purpose: tokenPurpose },
			})
			onTokenized(token.id)
			// Keep the spinner: the parent verifies the card, then navigates.
		} catch {
			setError('We could not secure your card. Please check the details and try again.')
		} finally {
			setIsSubmitting(false)
		}
	}

	return (
		<form onSubmit={handleSubmit} className="space-y-4">
			<div className="space-y-1.5">
				<label className="text-sm font-medium" htmlFor="billing-card-number">
					Card number
				</label>
				<div className={fieldClass}>
					<CardNumberElement
						id="billing-card-number"
						ref={numberRef}
						style={elementStyle}
						iconPosition="right"
						onChange={(event) => {
							setCardBrand(event.cardBrand)
							setComplete((c) => ({ ...c, number: event.complete }))
						}}
					/>
				</div>
			</div>

			<div className="grid grid-cols-2 gap-3">
				<div className="space-y-1.5">
					<label className="text-sm font-medium" htmlFor="billing-card-expiry">
						Expiry
					</label>
					<div className={fieldClass}>
						<CardExpirationDateElement
							id="billing-card-expiry"
							ref={expiryRef}
							style={elementStyle}
							onChange={(event) => setComplete((c) => ({ ...c, expiry: event.complete }))}
						/>
					</div>
				</div>
				<div className="space-y-1.5">
					<label className="text-sm font-medium" htmlFor="billing-card-cvc">
						CVC
					</label>
					<div className={fieldClass}>
						<CardVerificationCodeElement
							id="billing-card-cvc"
							ref={cvcRef}
							style={elementStyle}
							cardBrand={cardBrand}
							onChange={(event) => setComplete((c) => ({ ...c, cvc: event.complete }))}
						/>
					</div>
				</div>
			</div>

			{shownError && (
				<div className="bg-destructive/10 text-destructive flex items-start gap-2 rounded-md p-3 text-sm">
					<AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
					<span>{shownError}</span>
				</div>
			)}

			<Button type="submit" className="w-full" size="lg" disabled={busy}>
				{busy ? (
					<span className="flex items-center gap-2">
						<Loader2 className="h-4 w-4 animate-spin" />
						Verifying your card…
					</span>
				) : (
					<span className="flex items-center gap-2">
						<Lock className="h-4 w-4" />
						{submitLabel ?? 'Save card & activate'}
					</span>
				)}
			</Button>

			<p className="text-muted-foreground flex items-center justify-center gap-1.5 text-center text-xs">
				<Lock className="h-3 w-3" />
				Encrypted card vault · $0 verification, no charge today
			</p>
		</form>
	)
}
